// Mapa de la flota del proveedor sobre Google Maps JavaScript (PP-51). Mismo
// SDK, cargador, encuadre y tamanos por zoom que el mapa de la torre
// (@ucorp/web-map); lo que cambia es lo que se dibuja: solo vehiculos, sin
// paradas ni recorridos (la lectura multiplanta no los trae, ADR-025 §3).
//
// - Un AdvancedMarkerElement por vehiculo (DOM, sin innerHTML): silueta que
//   rota con el rumbo y el codigo de movil debajo. Azul con viaje, gris sin.
// - Entre reportes el marcador se desliza en linea recta hasta el nuevo
//   (glide de @ucorp/web-map), con UN rAF para todos. Nunca extrapola.
// - El encuadre muestra a toda la flota visible y se rehace cuando cambia el
//   alcance (filtro o planta), no en cada encuesta: el operador puede mover
//   el mapa sin que lo devuelvan.
// - La atribucion la dibuja el SDK y ningun panel propio la tapa (ADR-020 R6).
//
// Google recibe la zona visible y nada mas: las posiciones se dibujan en el
// navegador (regla 8). El SDK llega por el adaptador inyectable: los tests
// usan uno falso y no bajan nada de Google.
import { useEffect, useRef, useState } from "react";
import type { VehicleType } from "@ucorp/contracts";
import {
  glideFrame,
  initialCamera,
  markerSizing,
  onGlideReport,
  vehicleSilhouette,
  type GlideState,
  type GoogleMapsLoader,
  type GoogleMapsSdk,
} from "@ucorp/web-map";
import { boundsCenter, type Bounds } from "../logic/fleet-map.js";

/** Lo que el mapa necesita de un vehiculo; la seccion lo arma desde el contrato. */
export interface FleetMapVehicle {
  readonly key: string;
  readonly code: string;
  readonly type: VehicleType;
  readonly coordinates: { readonly lat: number; readonly lng: number };
  /** Instante del reporte: el mismo instante no reinicia el deslizamiento. */
  readonly instant: string;
  /** Texto del tooltip y del lector de pantalla. */
  readonly title: string;
  readonly onTrip: boolean;
}

export type FleetMapStatus = "loading" | "ready" | "unavailable";

interface Props {
  apiKey: string;
  mapId: string;
  loader: GoogleMapsLoader;
  vehicles: readonly FleetMapVehicle[];
  selected: string | null;
  onSelect: (key: string | null) => void;
  /** Recuadro a encuadrar; cambia junto con `fitKey`. */
  bounds: Bounds | null;
  /** Cambia cuando cambia el alcance (filtro o planta): vuelve a encuadrar. */
  fitKey: string;
  /** Duracion del deslizamiento: la cadencia con la que llegan los reportes. */
  glideMs: number;
}

/** Baldosa de Google: el mundo mide 256 px a zoom 0. */
const GOOGLE_TILE_PX = 256;
/** Colores de identidad (styles.css): acero con viaje, tinta suave sin viaje. */
const COLOR_ON_TRIP = "#2563eb";
const COLOR_IDLE = "#55637a";

interface Marker {
  marker: google.maps.marker.AdvancedMarkerElement;
  box: HTMLElement;
  rotor: HTMLElement;
  img: HTMLImageElement;
  glide: GlideState;
  onTrip: boolean;
  type: VehicleType;
}

function containerSize(el: HTMLElement): { width: number; height: number } {
  const box = el.getBoundingClientRect();
  return { width: box.width, height: box.height };
}

function paint(m: Marker, type: VehicleType, onTrip: boolean): void {
  const sprite = vehicleSilhouette(type, onTrip ? COLOR_ON_TRIP : COLOR_IDLE);
  m.img.src = sprite.url;
  m.img.width = sprite.width;
  m.img.height = sprite.height;
  m.box.classList.toggle("con-viaje", onTrip);
  m.onTrip = onTrip;
  m.type = type;
}

export function GoogleFleetMap({ apiKey, mapId, loader, vehicles, selected, onSelect, bounds, fitKey, glideMs }: Props): React.JSX.Element {
  const [sdk, setSdk] = useState<GoogleMapsSdk | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<Map<string, Marker>>(new Map());
  const selectedRef = useRef(selected);
  const onSelectRef = useRef(onSelect);
  const boundsRef = useRef(bounds);
  useEffect(() => {
    onSelectRef.current = onSelect;
    boundsRef.current = bounds;
  }, [onSelect, bounds]);

  // ---------- carga del SDK ----------

  useEffect(() => {
    let active = true;
    const offAuth = loader.onAuthFailure(() => {
      if (active) setFailed(true);
    });
    loader.load(apiKey).then(
      (loaded) => {
        if (active) setSdk(loaded);
      },
      () => {
        // Red, CSP o clave: el motivo no se muestra (puede traer la URL con la clave).
        if (active) setFailed(true);
      },
    );
    return () => {
      active = false;
      offAuth();
    };
  }, [loader, apiKey]);

  // ---------- mapa y tamanos por zoom ----------

  useEffect(() => {
    const container = containerRef.current;
    if (sdk === null || container === null) return;
    const start = boundsRef.current;
    const first = start === null ? null : initialCamera(start, boundsCenter(start), containerSize(container), GOOGLE_TILE_PX);
    const created = new sdk.Map(container, {
      mapId,
      // El portal es la superficie en base clara (identidad, styles.css).
      colorScheme: "LIGHT",
      center: first?.center ?? (start === null ? { lat: 0, lng: 0 } : boundsCenter(start)),
      zoom: first?.zoom ?? 12,
      isFractionalZoomEnabled: true,
      disableDefaultUI: true,
      zoomControl: true,
      // Los POI de Google no abren su ficha: el portal no es un buscador.
      clickableIcons: false,
      gestureHandling: "greedy",
      // Norte arriba: el rumbo de las siluetas lo asume.
      headingInteractionEnabled: false,
      tiltInteractionEnabled: false,
    });
    const applySizing = (): void => {
      const s = markerSizing(created.getZoom() ?? 12);
      container.style.setProperty("--escala-vehiculo", s.vehicleScale.toFixed(3));
      container.style.setProperty("--escala-vehiculo-sel", s.selectedVehicleScale.toFixed(3));
    };
    applySizing();
    const listeners = [
      created.addListener("zoom_changed", applySizing),
      // Tocar el mapa fuera de un vehiculo cierra la ficha.
      created.addListener("click", () => {
        if (selectedRef.current !== null) onSelectRef.current(null);
      }),
    ];
    setMap(created);
    const markers = markersRef.current;
    return () => {
      for (const l of listeners) l.remove();
      for (const m of markers.values()) m.marker.map = null;
      markers.clear();
      setMap(null);
    };
  }, [sdk, mapId]);

  // ---------- encuadre cuando cambia el alcance ----------

  useEffect(() => {
    const container = containerRef.current;
    const target = boundsRef.current;
    if (map === null || container === null || target === null) return;
    const camera = initialCamera(target, boundsCenter(target), containerSize(container), GOOGLE_TILE_PX);
    if (camera !== null) map.moveCamera(camera);
    // Solo el alcance reencuadra: una encuesta nueva no mueve el mapa.
  }, [map, fitKey]);

  // ---------- cada encuesta: alta, baja y reporte de cada vehiculo ----------

  useEffect(() => {
    if (sdk === null || map === null) return;
    const now = Date.now();
    const wanted = new Set<string>();
    for (const v of vehicles) {
      wanted.add(v.key);
      const existing = markersRef.current.get(v.key);
      if (existing !== undefined) {
        existing.glide = onGlideReport(existing.glide, v, now, glideMs);
        if (existing.onTrip !== v.onTrip || existing.type !== v.type) paint(existing, v.type, v.onTrip);
        existing.marker.title = v.title;
        continue;
      }
      const box = document.createElement("div");
      box.className = "flota-vehiculo";
      box.dataset["mapFeature"] = "vehicle";
      const rotor = document.createElement("div");
      rotor.className = "flota-vehiculo-rotor";
      const img = document.createElement("img");
      img.alt = "";
      img.draggable = false;
      rotor.append(img);
      const label = document.createElement("span");
      label.className = "flota-vehiculo-codigo";
      label.textContent = v.code;
      box.append(rotor, label);
      const glide = onGlideReport(undefined, v, now, glideMs);
      const marker = new sdk.AdvancedMarkerElement({
        map,
        position: v.coordinates,
        content: box,
        title: v.title,
        gmpClickable: true,
        anchorLeft: "-50%",
        anchorTop: "-50%",
        zIndex: selectedRef.current === v.key ? 20 : 10,
      });
      const key = v.key;
      marker.addEventListener("gmp-click", () => {
        onSelectRef.current(selectedRef.current === key ? null : key);
      });
      const created: Marker = { marker, box, rotor, img, glide, onTrip: v.onTrip, type: v.type };
      paint(created, v.type, v.onTrip);
      if (selectedRef.current === key) box.classList.add("seleccionado");
      markersRef.current.set(key, created);
    }
    for (const [key, m] of markersRef.current) {
      if (wanted.has(key)) continue;
      m.marker.map = null;
      markersRef.current.delete(key);
    }
  }, [sdk, map, vehicles, glideMs]);

  // ---------- animacion: UN rAF mueve todos los vehiculos ----------

  useEffect(() => {
    if (map === null) return;
    let frame = 0;
    const animate = (): void => {
      frame = requestAnimationFrame(animate);
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      for (const m of markersRef.current.values()) {
        const f = glideFrame(m.glide, now);
        m.marker.position = { lat: f.lat, lng: f.lng };
        m.rotor.style.transform = `rotate(${f.heading.toFixed(1)}deg)`;
      }
    };
    frame = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [map]);

  // ---------- seleccion: resaltado y, si quedo afuera, traerlo ----------

  useEffect(() => {
    selectedRef.current = selected;
    for (const [key, m] of markersRef.current) {
      const isSelected = key === selected;
      m.box.classList.toggle("seleccionado", isSelected);
      m.marker.zIndex = isSelected ? 20 : 10;
    }
    if (map === null || selected === null) return;
    const m = markersRef.current.get(selected);
    const visible = map.getBounds();
    if (m === undefined) return;
    const frame = glideFrame(m.glide, Date.now());
    const at = { lat: frame.lat, lng: frame.lng };
    if (visible === undefined || visible === null || !visible.contains(at)) map.panTo(at);
  }, [selected, map]);

  const status: FleetMapStatus = failed ? "unavailable" : map === null ? "loading" : "ready";

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" role="application" aria-label="Mapa de la flota" aria-busy={status === "loading"} data-map-status={status} />
      {status !== "ready" && (
        <div role="status" className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-pagina/90 p-4 text-center">
          <p className="m-0 text-sm font-semibold text-texto">{status === "loading" ? "Cargando mapa…" : "Mapa no disponible"}</p>
          {status === "unavailable" && <p className="m-0 text-sm text-texto-suave">No se pudo cargar Google Maps. La lista de la flota sigue funcionando.</p>}
        </div>
      )}
    </div>
  );
}
